import { interviewQueries, researchQueries, planQueries } from '@/db/queries';
import { generatePDFFromInterview } from '@/agents/pdf/generator';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Interview = Database['public']['Tables']['interviews']['Row'];

/**
 * Workflow execution result
 */
export interface WorkflowResult {
  success: boolean;
  planId?: string;
  pdfUrl?: string;
  errors: string[];
  warnings: string[];
}

/**
 * Research results structure
 */
interface ResearchResults {
  government_resources: any;
  risk_assessment: any;
  emergency_services: any;
  community_resources: any;
}

/**
 * End-to-End Emergency Plan Generation Workflow
 *
 * Orchestrates complete flow: Interview → Research → PDF → Delivery
 *
 * Phase 1: Interview (already complete when this runs)
 * Phase 2: Research Swarm (via edge function)
 * Phase 3: Report Compilation (via edge function)  
 * Phase 4: PDF Generation (client-side React-PDF)
 * Phase 5: Delivery (database update)
 */
export class PlanWorkflow {
  private interviewId: string;
  private interview: Interview | null = null;
  private errors: string[] = [];
  private warnings: string[] = [];

  constructor(interviewId: string) {
    this.interviewId = interviewId;
  }

  /**
   * Execute complete workflow from start to finish
   */
  async execute(): Promise<WorkflowResult> {
    console.log('🚀 Starting plan generation workflow for interview:', this.interviewId);

    try {
      // Step 1: Validate interview data (0-10%)
      console.log('📋 Step 1/5: Validating interview data...');
      await this.validateInterview();

      // Step 2: Execute research swarm (10-60%)
      console.log('🔍 Step 2/5: Executing research swarm...');
      const researchResults = await this.executeResearch();

      // Step 3: Compile plan data (60-80%)
      console.log('📝 Step 3/5: Compiling plan data...');
      const planData = await this.compilePlanData(researchResults);

      // Step 4: Generate PDF (80-95%)
      console.log('📄 Step 4/5: Generating PDF...');
      const pdfResult = await this.generatePDF(planData);

      // Step 5: Deliver plan (95-100%)
      console.log('✉️ Step 5/5: Delivering plan...');
      const planId = await this.deliverPlan(pdfResult);

      console.log('✅ Workflow complete! Plan ID:', planId);

      return {
        success: true,
        planId,
        pdfUrl: pdfResult.pdfUrl,
        errors: this.errors,
        warnings: this.warnings,
      };

    } catch (error: any) {
      console.error('❌ Workflow failed:', error);
      this.errors.push(error.message || 'Unknown error occurred');

      return {
        success: false,
        errors: this.errors,
        warnings: this.warnings,
      };
    }
  }

  /**
   * Step 1: Validate Interview Data
   */
  private async validateInterview(): Promise<void> {
    this.interview = await interviewQueries.getById(this.interviewId);

    if (!this.interview) {
      throw new Error('Interview not found');
    }

    if (!this.interview.location) {
      throw new Error('Location data is required');
    }

    const location = this.interview.location as any;
    if (!location.city || !location.state) {
      throw new Error('Complete location (city and state) is required');
    }

    if (!this.interview.emergency_types || this.interview.emergency_types.length === 0) {
      throw new Error('At least one emergency type must be selected');
    }

    if (!this.interview.property_type) {
      throw new Error('Property type is required');
    }

    if (!this.interview.household_size || this.interview.household_size < 1) {
      throw new Error('Household size must be at least 1');
    }

    console.log('✅ Interview validation passed');
  }

  /**
   * Step 2: Execute Research Swarm
   * 
   * Calls the research-swarm edge function which:
   * - Uses Perplexity for real-time web search
   * - Runs 4 parallel research tasks
   * - Returns structured research data
   */
  private async executeResearch(): Promise<ResearchResults> {
    try {
      const location = this.interview?.location as any;
      
      const response = await supabase.functions.invoke('research-swarm', {
        body: {
          interviewId: this.interviewId,
          location: {
            address: location?.address,
            city: location?.city,
            state: location?.state,
            country: location?.country,
          },
          emergencyTypes: this.interview?.emergency_types,
          householdSize: this.interview?.household_size,
        },
      });

      if (response.error) {
        console.warn('Research swarm error:', response.error);
        this.warnings.push('Research phase encountered issues - using fallback data');
        return this.getFallbackResearch();
      }

      console.log('✅ Research phase complete');
      return response.data;

    } catch (error: any) {
      console.error('❌ Research phase failed:', error);
      this.warnings.push('Research phase failed - using minimal fallback data');
      return this.getFallbackResearch();
    }
  }

  /**
   * Step 3: Compile Plan Data
   */
  private async compilePlanData(research: ResearchResults): Promise<any> {
    if (!this.interview) {
      throw new Error('Interview not loaded');
    }

    const location = this.interview.location as any;
    const household = { size: this.interview.household_size, ages: this.interview.household_ages };
    const property = (this.interview.property_details as any) || {};
    const medicalNeeds = this.interview.medical_needs as any;
    const aiInsights = (this.interview.ai_insights as any) || {};
    const nuancedDetails = (this.interview.nuanced_details as any) || {};

    const planData = {
      user_info: {
        name: this.interview.name,
        email: this.interview.email,
        location,
        household,
        property,
        emergency_types: this.interview.emergency_types,
        medical_needs: medicalNeeds || {},
        budget: this.interview.budget,
      },
      emergency_contacts: {
        primary: location?.emergency_contact || null,
      },
      research: {
        government_resources: research.government_resources,
        risk_assessment: research.risk_assessment,
        emergency_services: research.emergency_services,
        community_resources: research.community_resources,
      },
      ai_insights: aiInsights,
      nuanced_details: nuancedDetails,
      generated_at: new Date().toISOString(),
      version: '2.0',
      warnings: this.warnings,
    };

    console.log('✅ Plan data compiled');
    return planData;
  }

  /**
   * Step 4: Generate PDF
   */
  private async generatePDF(planData: any): Promise<{ pdfUrl: string; fileSize: number; planId: string }> {
    try {
      const result = await generatePDFFromInterview(this.interviewId, this.interview!.user_id);
      console.log(`✅ PDF generated: ${result.fileSize} bytes`);
      return result;
    } catch (error: any) {
      console.error('❌ PDF generation failed:', error);
      throw new Error(`Failed to generate PDF: ${error.message}`);
    }
  }

  /**
   * Step 5: Deliver Plan
   */
  private async deliverPlan(pdfResult: { planId: string; pdfUrl: string }): Promise<string> {
    try {
      if (this.interview?.status !== 'completed') {
        await interviewQueries.complete(this.interviewId);
      }
      console.log('✅ Plan delivered successfully');
      return pdfResult.planId;
    } catch (error: any) {
      console.error('❌ Plan delivery failed:', error);
      throw new Error(`Failed to deliver plan: ${error.message}`);
    }
  }

  /**
   * Fallback research data when research-swarm fails
   */
  private getFallbackResearch(): ResearchResults {
    const location = (this.interview?.location as any) || {};
    
    return {
      government_resources: {
        note: 'Research unavailable. Visit ready.gov for official emergency preparedness guidance.',
        fema_region: null,
        state_agency: null,
      },
      risk_assessment: {
        note: `Risk assessment unavailable for ${location.city || 'your area'}. Consult local emergency management.`,
        earthquake_risk: 'unknown',
        flood_risk: 'unknown',
        wildfire_risk: 'unknown',
      },
      emergency_services: {
        note: 'Emergency services search failed. Call 911 for emergencies, 211 for local resources.',
        hospitals: [],
        shelters: [],
      },
      community_resources: {
        note: 'Community resources unavailable. Contact local Red Cross chapter for assistance.',
        red_cross: null,
        supply_stores: [],
      },
    };
  }
}
